import { useCallback, ChangeEvent, MouseEvent, useState, ReactNode } from 'react';
import { DisclosureModel } from '../../GraphQL.LoanApplication';
import { Dialog } from '../../shared/Dialog';
import { HelpPop } from '../../shared/HelpPop';
import { Node, parseHtml } from '../../shared/parseHtml';
import { Spinner } from '../../shared/Spinner';
import { ValidationMessage } from '../../shared/ValidationMessage';
import { Checkbox } from '../../shared/Checkbox';
import { renderVariables } from './renderVariables';
import { LOAN_WITH_ANOTHER_LENDER, HAS_EXISTING_PAYMENT_PLAN } from './disclosureConstants';

interface DisclosureProps {
    disclosure: Partial<DisclosureModel>;
    onChange?: (disclosure: Partial<DisclosureModel>) => void;
    showError?: boolean;
    disabled?: boolean;
    variables?: Record<string, string>;
    disclosureClassName?: string;
}

export function Disclosure(props: DisclosureProps) {
    const { disclosure, onChange, disabled } = props;
    const hasOptions = !!disclosure.radioOptions?.length;

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            if (disclosure.needsRead && e.currentTarget.checked) {
                setShowDisclosureModal(true);
            } else {
                disclosure.agree = e.currentTarget.checked;
                onChange?.({ ...disclosure, agree: e.currentTarget.checked, selectedValue: e.currentTarget.value });
            }
        },
        [onChange, disclosure]
    );

    const [dialog, setDialog] = useState<{ title: string; children: ReactNode[] }>();
    const [showDisclosureModal, setShowDisclosureModal] = useState(false);
    const [showExpanded, setShowExpanded] = useState(false);

    const title = disclosure?.id?.split(/(?=[A-Z])/).join(' '); //getting spaces in

    const handleCreateElement = useCallback((node: Node) => {
        if (node.tagName !== 'a') return node;
        const { target, href, rel } = node.attributes;
        if (typeof href !== 'string') return node;
        if (target === '_blank' && (rel === 'noopener' || !/((localizedcontent\.axd)|DynamicContent|Content)/i.test(href))) return node;

        node.attributes.onClick = async (e: MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            setDialog({ title: 'Disclosure', children: [<Spinner key="spinner" />] });
            const response = await fetch(href);
            const html = await response.text();
            let title = '';
            const children =
                parseHtml(html, (node) => {
                    if (!title && node.tagName.match(/^H/i)) {
                        title = node.children?.find((x) => typeof x === 'string' && x.length > 0)?.toString() ?? '';
                        return title ? undefined : node;
                    }
                    return node;
                }) ?? [];
            setDialog({ title, children });
        };
        return node;
    }, []);

    const handleDialogClose = useCallback(() => setDialog(undefined), []);

    const handleDisclosureDialogClose = () => {
        setShowExpanded(false);
        setShowDisclosureModal(false);
    };

    const renderDisclosureModal = () => {
        const downloadPath = `/Content/pdf/${disclosure.id}.pdf`;
        return (
            <Dialog size="lg" showCloseControl onClosing={handleDisclosureDialogClose}>
                <div className="accent-box" style={{ marginTop: '-40px' }}>
                    {!showExpanded && <div className="accent-box-header p-3">{title}</div>}
                    <div className="accent-box-body p-4">
                        <iframe id={`doc-iframe-${disclosure.id}`} title={`doc-iframe-${disclosure.id}`} src={disclosure.documentContent} height={showExpanded ? "600px" : "400px"} width="100%"></iframe>
                        {!showExpanded && (
                            <>
                                <div className="row pt-1">
                                    <div className="p-1 text-center flex-grow-1">
                                        <button type="button" className="no-underline btn-expand" onClick={onExpandClick}>
                                            Expand
                                        </button>
                                    </div>
                                    <div className="p-1 text-center flex-grow-1">
                                        <button type="button" className="no-underline btn-print" onClick={onPrintClick}>
                                            Print
                                        </button>
                                    </div>
                                    {downloadPath != null && (
                                        <div className="p-1 text-center flex-grow-1">
                                            <a
                                                id={`btnDownload${disclosure.id}`}
                                                href={downloadPath}
                                                className="no-underline"
                                                rel="noreferrer"
                                                download={disclosure?.id}
                                            >
                                                Save
                                            </a>
                                        </div>
                                    )}
                                </div>
                                <br></br>
                                <button type="button" onClick={onAcceptClick} className="modal-ok-button print:hidden disclosure-modal-accept-btn" id={`btnOk-${disclosure?.id}`}>
                                    Agree & Continue
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </Dialog>
        );
    };

    const onAcceptClick = () => {
        onChange?.({ ...disclosure, agree: true, selectedValue: 'checked' });
        setShowDisclosureModal(false);
    };

    const onPrintClick = () => {
        // @ts-ignore
        const iframe = document.frames ? document.frames[`doc-iframe-${disclosure.id}`] : document.getElementById(`doc-iframe-${disclosure.id}`);
        const iframeWindow = iframe.contentWindow || iframe;
        iframe.focus();
        iframeWindow.print();
        return false;
    };

    const onExpandClick = () => {
        setShowExpanded(true);
    };

    return (
        <>
            {disclosure.header && <h3>{disclosure.header}</h3>}
            {disclosure.subHeader && <div className="mb-5">{disclosure.subHeader}</div>}
            <div className={props.disclosureClassName ?? 'mb-5'} id={`disclosure_${disclosure.id}`}>
                {hasOptions && (
                    <>
                        <div>
                            {parseHtml(disclosure.content, handleCreateElement)}
                            {disclosure.help && <HelpPop text={disclosure.help} />}
                        </div>
                        <div className="form-inline">
                            {disclosure.radioOptions?.map((option) => (
                                <label className="custom-control custom-radio mt-1 mb-2 ml-2">
                                    <input
                                        type="radio"
                                        value={option.value.toString()}
                                        className="custom-control-input js-checked"
                                        onChange={handleChange}
                                        checked={String(option.value) === disclosure.selectedValue}
                                        disabled={disabled}
                                    />
                                    <span className="custom-control-indicator" />
                                    <span className="custom-control-label">{parseHtml(option.key, handleCreateElement)}</span>
                                </label>
                            ))}
                        </div>
                    </>
                )}

                {!hasOptions && (
                    <Checkbox className="js-checked" onChange={handleChange} checked={disclosure.agree} disabled={disabled}>
                        {parseHtml(renderVariables(disclosure.content, props.variables), handleCreateElement)}
                        {disclosure.help && <HelpPop text={disclosure.help} />}
                    </Checkbox>
                )}

                {props.showError &&
                    disclosure.required &&
                    (!disclosure.agree ||
                        (hasOptions && (disclosure?.selectedValue?.length ?? 0) === 0) ||
                        (disclosure.id === LOAN_WITH_ANOTHER_LENDER && disclosure.selectedValue === HAS_EXISTING_PAYMENT_PLAN)) && (
                        <ValidationMessage
                            message={
                                disclosure.selectedValue !== HAS_EXISTING_PAYMENT_PLAN
                                    ? disclosure.validation ?? 'This is required'
                                    : disclosure.moreThanOneInstallmentPlan ?? 'This is required'
                            }
                        />
                    )}
            </div>

            {showDisclosureModal && renderDisclosureModal()}

            {!!dialog && (
                <Dialog
                    size="lg"
                    title={dialog.title}
                    showCloseControl={true}
                    onClosing={handleDialogClose}
                    children={dialog.children}
                    footer={
                        <button type="button" className="btn btn-success mr-0 ml-auto" onClick={handleDialogClose}>
                            OK
                        </button>
                    }
                />
            )}
        </>
    );
}
